import { IRecommendation } from '@/interfaces/recommendation'
import { getBlockedRoutes } from '@/shared/ApiServiceV2'
import { environment } from '@/shared/consts/environment'
import { getCookie } from '@/shared/utils'
import { checkAuthorizedUser } from '@/shared/validations/user'
import axios from 'axios'
import { IUser } from 'interfaces'
import './axiosAuthMiddleware'
import { errorMiddleware } from './errorMiddleware'

const apiUrl = `${environment.apiUrl}/api/v1/users`

/**
 * ----------------------------------------
 * Create
 * ----------------------------------------
 */

// --- createUser
export const createUser = async (user: IUser) => {
  try {
    const res = await axios.post(`${apiUrl}`, user)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

/**
 * ----------------------------------------
 * Retrieve
 * ----------------------------------------
 */

// --- getUser
export const getUser = async (token: string) => {
  const res = await fetch(`${environment.apiUrl}/api/v1/users`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    next: { revalidate: 3600 },
  })

  return res.json()
}

// --- getUserByEmail
export const getUserByEmail = async (email: string) => {
  const res = await fetch(`${environment.apiUrl}/api/v1/users/email/${email}`, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

  return res.json()
}

export const getUsersByIds = async (ids: string[]) => {
  // TODO: joxxe274 Implement this
  try {
    const res = await axios.post(`${apiUrl}/retrieve-users`, ids)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const getUserTotalAudience = async (id: string) => {
  try {
    const res = await axios.get(`${apiUrl}/audience/${id}`)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const getUserRecommendations = async (userId) => {
  try {
    const res = await axios.get(`${apiUrl}/recommendations/${userId}`)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const getColdProfileById = async (id) => {
  try {
    const res = await axios.get(`${apiUrl}/recommendations/cprofile/${id}`)
    return res.data as IRecommendation
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const getUserExistsById = async (id: string) => {
  try {
    const res = await axios.get(`${apiUrl}/user-exists/${id}`)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const getUserById = async (id: string) => {
  try {
    const res = await axios.get(`${apiUrl}/id/${id}`)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

// --- getUserByUsername
export const getUserByUsername = async (username: string) => {
  try {
    const headers = {}
    const res = await axios.get(`${apiUrl}/${username}`, {
      headers,
    })
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

interface GetUserByUsernameWithTokenAndUrlParams {
  username: string
  url: string
  token?: string
}
// --- getUserByUsernameWithTokenAndUrl
export const getUserByUsernameWithTokenAndUrl = async ({
  username,
  url,
  token,
}: GetUserByUsernameWithTokenAndUrlParams) => {
  try {
    let headers = {}
    if (token) {
      headers = {
        Authorization: `Bearer ${token}`,
        Referer: url,
        xUrl: url,
      }
    } else {
      headers = {
        xUrl: url,
      }
    }
    const res = await axios.get(`${apiUrl}/${username}`, {
      headers,
    })
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

// --- getUserByUsernameWithToken
export const getUserByUsernameWithToken = async (
  username: string,
  token: string
) => {
  try {
    let headers = {}
    headers = {
      Authorization: `Bearer ${token}`,
    }
    const res = await axios.get(`${apiUrl}/${username}`, {
      headers,
    })
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

/**
 * ----------------------------------------
 * Update
 * ----------------------------------------
 */

// --- completeUser
export const completeUser = async () => {
  try {
    const res = await axios.post(`${apiUrl}/complete-register`)
    // ToDo: Change backend to pick random avatar value
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

// --- updateUserByEmail
export const updateUserByEmail = async (user: IUser) => {
  try {
    const res = await axios.patch(`${apiUrl}`, user)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const removeUserAvatar = async () => {
  try {
    const res = await axios.post(`${apiUrl}/remove-avatar`)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const uploadUserAvatar = async (file: File) => {
  try {
    const formData = new FormData()

    formData.append('file', file)

    const res = await axios.post(`${apiUrl}/upload-avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const uploadUserImage = async (file: File) => {
  try {
    const formData = new FormData()

    formData.append('file', file)

    const res = await axios.post(`${apiUrl}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

/**
 * ----------------------------------------
 * Delete
 * ----------------------------------------
 */

/**
 * ----------------------------------------
 * Validation
 * ----------------------------------------
 */

// --- emailExists
export const emailExists = async (email: string) => {
  try {
    const res = await axios.get(`${apiUrl}/email-exists/${email}`)
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

// --- usernameExists
export const usernameExists = async (username: string) => {
  try {
    const res = await axios.get(
      `${apiUrl}/username-exists-blocklist/${username}`
    )
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

// --- validateUserToken
export const validateUserToken = async (token?: string) => {
  try {
    let headers = {}
    if (token) {
      headers = {
        Authorization: `Bearer ${token}`,
      }
    }
    const res = await axios.get(`${apiUrl}`, { headers })
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}

export const getUserData = async (username: string, cookie: string) => {
  const tokenCookie = getCookie('token', cookie)

  let token = null

  try {
    token = JSON.parse(tokenCookie).access_token
  } catch (e) {
    token = null
  }

  // Do not refactor [getUserByUsernameWithToken, getBlockedRoutes] without review from leads
  const userData: IUser = await getUserByUsernameWithToken(
    String(username),
    token
  )
  let blockedRoutes: string[] = []
  if (token) {
    blockedRoutes = await getBlockedRoutes()
  }

  if (
    !userData ||
    (Array.isArray(blockedRoutes) &&
      blockedRoutes.includes(userData.username)) ||
    !checkAuthorizedUser(userData)
  ) {
    return false
  }

  return userData
}

export const isBlockedRoute = async (username: string) => {
  const blockedRoutes: string[] = await getBlockedRoutes()
  return blockedRoutes.includes(username)
}

/**
 * Others
 */

export const mixPanelTrack = async (url: string, profilePageId: string) => {
  try {
    const headers = {
      xUrl: url,
      profilePageId: profilePageId,
    }
    const res = await axios.get(`${apiUrl}/mixpanel/profile`, { headers })
    return res.data
  } catch (error) {
    return errorMiddleware(error)
  }
}
